<template>
    <modal name="make-payment" width="80%" height="auto" :scrollable="true" @before-close="onBeforeClose">
        <div class="modal-header-custom">
            <h3 class="modal-title-custom">
                <i class="mdi mdi-cash-multiple"></i>
                Liste des demandes de paiement
            </h3>
            <button @click="hide('make-payment')" class="btn btn-close">
                <i class="mdi mdi-close"></i>
            </button>
        </div>

        <div class="loader" v-if="loader"></div>

        <div class="modal-body-custom">
            <!-- Payment Requests Grid -->
            <div class="admin-grid" v-if="paymentRequests.length !== 0">
                <div v-for="paymentRequest in paymentRequests" :key="paymentRequest.id" class="admin-card payment-request-card">
                    <div class="admin-card-header">
                        <div class="request-header-left">
                            <div class="request-amount">
                                <i class="mdi mdi-cash"></i>
                                <span class="amount-value">
                                    {{
                                        paymentRequest.montant
                                            | currency(" DA", 0, { symbolOnLeft: false })
                                    }}
                                </span>
                            </div>
                            <div class="request-id">ID: {{ paymentRequest.id }}</div>
                        </div>
                        <div class="request-status">
                            <span v-if="paymentRequest.state === null" class="status-badge status-pending">
                                <i class="mdi mdi-clock-outline"></i>
                                En attente
                            </span>
                            <span v-if="paymentRequest.state == 0" class="status-badge status-refused">
                                <i class="mdi mdi-close-circle"></i>
                                Refusé
                            </span>
                            <span v-if="paymentRequest.state == 1" class="status-badge status-done">
                                <i class="mdi mdi-check-circle"></i>
                                Payé
                            </span>
                        </div>
                    </div>
                    <div class="admin-card-body">
                        <div class="bank-info-grid">
                            <div class="bank-info-item">
                                <label class="admin-card-label">
                                    <i class="mdi mdi-credit-card"></i>
                                    IBAN
                                </label>
                                <div class="bank-info-value">{{ paymentRequest.payment_information.iban }}</div>
                            </div>
                            <div class="bank-info-item">
                                <label class="admin-card-label">
                                    <i class="mdi mdi-bank"></i>
                                    BIC
                                </label>
                                <div class="bank-info-value">{{ paymentRequest.payment_information.bic }}</div>
                            </div>
                        </div>
                        <div class="admin-card-section" v-if="paymentRequest.state === null">
                            <div class="action-buttons">
                                <button class="btn btn-success btn-action" @click="pay(paymentRequest.id, 'confirm')">
                                    <i class="mdi mdi-check"></i>
                                    Confirmer le paiement
                                </button>
                                <button class="btn btn-danger btn-action" @click="pay(paymentRequest.id, 'refuse')">
                                    <i class="mdi mdi-close"></i>
                                    Refuser
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-else class="admin-empty-state">
                <svg class="admin-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                    <line x1="2" y1="10" x2="22" y2="10"></line>
                </svg>
                <p class="admin-empty-text">Aucune demande de paiement</p>
            </div>
        </div>

        <div class="modal-footer-custom">
            <button @click="hide('make-payment')" class="btn btn-secondary">
                <i class="mdi mdi-close"></i>
                Fermer
            </button>
        </div>

        <notifications style="top:5%;" width="35%" group="error" position="top center" />
        <notifications style="top:5%;" width="35%" group="success" position="top center" />
    </modal>
</template>

<script>
export default {
    props: {
        walletId: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            paymentRequests: [],
            loader: false,
        };
    },

    mounted() {
        if (undefined !== this.walletId) {
            this.getPaymentRequest(this.walletId);
        }
    },

    methods: {
        hide(modalName) {
            this.$modal.hide(modalName);
        },

        onBeforeClose() {
            this.$emit('closed');
        },

        async getPaymentRequest(walletId) {
            try {
                this.loader = true;
                const response = await this.$axios.get(`admin/wallet/paymentRequest/${walletId}`);
                this.paymentRequests = response.data;
                this.loader = false;
            } catch (err) {
                this.loader = false;
                console.error("Error loading payment requests:", err);
                this.$notify({
                    group: "error",
                    title: "Erreur lors du chargement des demandes",
                    type: "error",
                });
            }
        },

        async pay(id, state) {
            const confirmMessage = state === 'confirm' 
                ? 'Êtes-vous sûr de vouloir confirmer ce paiement ?' 
                : 'Êtes-vous sûr de vouloir refuser cette demande ?';
            
            if (!confirm(confirmMessage)) {
                return;
            }

            try {
                this.loader = true;
                await this.$axios.get(`admin/wallet/respondTo/${state}/${id}`);
                this.loader = false;
                
                this.$notify({
                    group: "success",
                    title: state === 'confirm' ? "Paiement confirmé" : "Demande refusée",
                    type: "success",
                });
                
                this.getPaymentRequest(this.walletId);
            } catch (err) {
                this.loader = false;
                console.error("Error processing payment:", err);
                this.$notify({
                    group: "error",
                    title: "Erreur lors du traitement",
                    type: "error",
                });
            }
        },
    },
};
</script>

<style scoped src="@/assets/css/shared-admin-styles.css"></style>
<style scoped>
@import '~@/assets/css/colors.css';

/* Modal Structure */
.modal-header-custom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 32px;
    border-bottom: 1px solid var(--gray-200);
    background: white;
}

.modal-title-custom {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: var(--gray-900);
}

.modal-title-custom i {
    font-size: 24px;
    color: var(--primary);
}

.modal-body-custom {
    padding: 32px;
    max-height: 70vh;
    overflow-y: auto;
    background: var(--gray-50);
}

.modal-footer-custom {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 20px 32px;
    border-top: 1px solid var(--gray-200);
    background: white;
}

/* Payment Request Card Specific */
.payment-request-card .admin-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
}

.request-header-left {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.request-amount {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 20px;
    font-weight: 700;
    color: var(--gray-900);
}

.request-amount i {
    font-size: 28px;
    color: var(--success-100);
}

.amount-value {
    font-size: 22px;
}

.request-id {
    font-size: 12px;
    color: var(--gray-500);
    font-weight: 500;
}

.request-status {
    display: flex;
    align-items: center;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
}

.status-pending {
    background: var(--warning-light);
    color: var(--warning-300);
    border: 1px solid var(--color-id-300);
}

.status-refused {
    background: var(--danger-light);
    color: var(--color-oo-500);
    border: 1px solid var(--color-oo-200);
}

.status-done {
    background: var(--success-light);
    color: var(--success-400);
    border: 1px solid var(--success-100);
}

.status-badge i {
    font-size: 16px;
}

/* Bank Info Grid */
.bank-info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
}

.bank-info-item {
    padding: 12px;
    background: var(--gray-50);
    border-radius: 6px;
    border: 1px solid var(--gray-200);
}

.admin-card-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: var(--gray-500);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
}

.admin-card-label i {
    font-size: 14px;
}

.bank-info-value {
    font-size: 14px;
    font-weight: 600;
    color: var(--gray-900);
    font-family: monospace;
}

/* Action Buttons */
.action-buttons {
    display: flex;
    gap: 12px;
    padding-top: 8px;
}

.btn-action {
    flex: 1;
    padding: 10px 16px;
    font-size: 14px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.btn-action i {
    font-size: 16px;
}

/* Loader */
.loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;
}

/* Responsive */
@media (max-width: 768px) {
    .modal-header-custom,
    .modal-body-custom,
    .modal-footer-custom {
        padding: 20px;
    }

    .bank-info-grid {
        grid-template-columns: 1fr;
    }

    .action-buttons {
        flex-direction: column;
    }

    .btn-action {
        width: 100%;
    }

    .request-amount {
        font-size: 18px;
    }

    .amount-value {
        font-size: 18px;
    }
}

@media (max-width: 480px) {
    .payment-request-card .admin-card-header {
        flex-direction: column;
        gap: 12px;
    }

    .modal-title-custom {
        font-size: 16px;
    }

    .modal-title-custom i {
        font-size: 20px;
    }
}
</style>